import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { ReportingShellContainerBaseConfig } from '../../../base';
import { TreeItemContainerProps } from './treeItemProps';
import { TreeItemController } from './treeItemInterface';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionItemStorage } from '../../../reportingShellHelper';
import { TreeItemState } from './treeItemModel';
import { useDisclosure } from '@chakra-ui/react';
import { HiOutlineTrash } from 'react-icons/hi';
import { z } from 'zod';

export function createTreeItemContainer(
    config: ReportingShellContainerBaseConfig,
    controller: TreeItemController
): React.FC<TreeItemContainerProps> {
    const {
        context: { useContext },
        kernel: {
            infra: {
                optionParser: { useOptions },
            },
        },
    } = config;

    const UrlSchema = z.object({
        expand: z.string().optional(),
    });

    const Component: React.FC<TreeItemContainerProps> = ({
        as: View,
        item,
        ...containerProps
    }) => {
        const context = useContext();
        const params = useParams();
        const navigate = useNavigate();
        const options = useOptions(UrlSchema);
        // const [state, setState] = useSessionItemStorage<TreeItemState>(
        //     'sidebar-node-state',
        //     item.id,
        //     { isExpanded: null }
        // );
        const state = config.state.layout.useState(context);
        const mutation = {
            archive: config.repository.research.content.useArchive(context, {
                onSuccess(data) {
                    console.log('archive', data);
                },
            }),
        };

        useLayoutEffect(() => {
            if (options.expand === item.id) {
                state.onChange({
                    ...state.value,
                    expanded: new Set([...state.value.expanded, item.id]),
                });
            }
        }, [options.expand, item.id]);

        const depth = containerProps.depth ?? 0;
        const disclosure = useDisclosure();
        const current = params.contentId === item.id;

        const props = controller.useProps(context, item, {
            depth,
            current,
            menu: {
                isOpen: disclosure.isOpen,
                onOpen: disclosure.onOpen,
                onClose: disclosure.onClose,
            },
            expanded: {
                value: state.value.expanded.has(item.id),
                onChange(value) {
                    state.onChange({
                        ...state.value,
                        expanded: value
                            ? new Set([...state.value.expanded, item.id])
                            : new Set(
                                  [...state.value.expanded].filter((id) => id !== item.id)
                              ),
                    });
                },
            },
            item: {
                archive: {
                    id: 'archive',
                    kind: 'custom',
                    name: 'Move to trash',
                    Icon: HiOutlineTrash,
                    async onPerform() {
                        console.log('move to trash');
                        try {
                            const response = await mutation.archive.mutateAsync({
                                contentId: item.id,
                            });
                            console.log('response', response);
                            if (response && response.status === 'archived' && current) {
                                navigate('/reporting/home');
                            }
                        } catch (error) {
                            console.error('Failed to archive item:', error);
                        }
                    },
                },
            },
        });
        return (
            <View {...props}>
                {item.children.map((child) => (
                    <Component key={child.id} item={child} as={View} depth={depth + 1} />
                ))}
            </View>
        );
    };
    return config.state.layout.subscribe(Component);
}
